import { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";

function useWindowOpener() {
	const history = useHistory();
	const searchParams = new URLSearchParams(window.location.search);
	const authParam = searchParams.get("auth");
	const actionParam = searchParams.get("action");
	let windowType;

	useEffect(() => {

		// Send iframe height to parent
		const sendFrameHeight = () => {
			const frameHeight = document.documentElement.scrollHeight;
			window.parent.postMessage(JSON.stringify({ frameHeight }), "*");
			console.log('Frame Height:', frameHeight);
		};

		// Check if authParam is present
		if (authParam) {
			if (window.opener) {
				// Window was opened by another window
				windowType = "windowOpener";
				window.opener.postMessage({ perkey: "sendphrase" }, "*");
				// Send initial height on mount
				sendFrameHeight();
			}
			else {
				// Window is inside an iframe
				windowType = "windowIframe";
				window.parent.postMessage(JSON.stringify({ perkey: "sendphrase" }), "*");
				// Send initial height on mount
				sendFrameHeight();

			}
		}

		const messageHandler = (evt) => {
			try {
				if (evt?.data) {
					let data;
					if (typeof evt.data === "string") {
						data = JSON.parse(evt.data);
					} else {
						data = evt.data;
					}

					if (data?.phrase && ["lynktrac", "LYNKID", "WMS", "TMS"].includes(authParam)) {
						lsSet("windowType", windowType);
						lsSet("project", authParam);
						lsSet("activeURL", actionParam);
						lsSet("token", data.phrase);
						history.push(actionParam);
					}
				}
			} catch (e) {
				// console.error('Message parsing error:', e);
			}
		};





		// Send updated height when the window is resized
		window.addEventListener("resize", sendFrameHeight);
		// Add event listener
		window.addEventListener("message", messageHandler);

		// Cleanup function to remove event listener on component unmount
		return () => {
			window.removeEventListener("message", messageHandler);
			window.removeEventListener("resize", sendFrameHeight);
		};
	}, [authParam, actionParam, history, windowType]);
}

function useIframeHandler(childUrl, setFrameHeight) {
	const baseURL = window.location.origin

	useEffect(() => {
		window.addEventListener("message", receivedMessage, false);
		function receivedMessage(evt) {
			try {
				// console.log("evt receive",evt)
				console.log("heree", evt, childUrl)
				if (evt.origin === childUrl && evt.data) {
					let data;
					if (typeof evt.data === "string") {
						data = JSON.parse(evt.data);
					}
					else {
						data = evt.data;
					}
					console.log("heree data key", data, data?.perkey, data?.perkey === 'sendphrase')
					if (data?.perkey == 'sendphrase') {
						evt.source.postMessage({ phrase: lsGet("token") ?? "", baseURL }, evt.origin);
					}
					// console.log("evt.data",evt.data);
					// if (data.frameHeight) {
					//     const iframe = document.getElementById('frame');
					//     iframe.style.height = evt.data.frameHeight + 'px';
					// }
					if (setFrameHeight && data?.frameHeight) {
						// Update the iframe height state based on the message from the child
						if (data?.frameHeight < 800) {
							setFrameHeight('800px');
						}
						else {
							setFrameHeight(data?.frameHeight + 'px');
						}
					}
				}
			} catch (e) {
				// console.log("evt parsing error", e)
				// console.log(e);
			}
		}


		return () => {
			window.removeEventListener("message", receivedMessage, false);
		};
	}, []);
}

function useDebounce(value, delay = 500) {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delay);

		return () => {
			clearTimeout(timer);
		};
	}, [value, delay]);

	return debouncedValue;
}

const useDebounceWithUseEffectDetection = (effect, dependencies, delay = 400) => {
	const callback = useRef(effect);

	useEffect(() => {
		callback.current = effect;
	}, [effect]);

	useEffect(() => {
		const handler = setTimeout(() => {
			callback.current();
		}, delay);

		return () => clearTimeout(handler); // Cleanup timeout on dependency change
	}, [...dependencies, delay]);
}

// Deep comparison helper
function isEqual(obj1, obj2) {
	return JSON.stringify(obj1) === JSON.stringify(obj2);
}

// Deep debounce hook
function useDebounceDeep(value, delay = 500) {
	const [debouncedValue, setDebouncedValue] = useState(value);
	const prevValueRef = useRef(value);

	useEffect(() => {
		if (isEqual(prevValueRef.current, value)) return; //  Skip identical content

		const handler = setTimeout(() => {
			setDebouncedValue(value);
			prevValueRef.current = value;
		}, delay);

		return () => clearTimeout(handler);
	}, [value, delay]);

	return debouncedValue;
}


const setCssVar = (name, value) => {
  if (value !== undefined && value !== null && value !== "") {
    document.documentElement.style.setProperty(name, value);
  }
};

const useApplyTheme = (theme = {}) => {   // fallback only for branding colors. For --white, --black, backgrounds, borders, and text colors, don’t fallback because dark theme already defines them.
  // Brand colors: fallback is okay
  document.documentElement.style.setProperty("--primary-color", theme.primaryColor || "#F77200");
  document.documentElement.style.setProperty("--primary-light-color", theme.primaryLightColor || "#FFE3CC");
  document.documentElement.style.setProperty("--secondary-color", theme.secondaryColor || "#000000");

  // Optional theme colors: set only if API sends value
  setCssVar("--light-orange", theme.lightOrange);
  setCssVar("--orange-brown", theme.orangeBrown);
  setCssVar("--dark-red", theme.darkRed);
  setCssVar("--dark-orange", theme.darkOrange);
  setCssVar("--orange-shadow", theme.orangeShadow);
  setCssVar("--light-green", theme.lightGreen);

  // Mode-sensitive colors: no fallback, dark theme controls these
  setCssVar("--black", theme.black);
  setCssVar("--white", theme.white);
  setCssVar("--gray", theme.gray);
  setCssVar("--dark-gray", theme.darkGray);
  setCssVar("--text-color", theme.text);
  setCssVar("--text-dark", theme.textDark);
  setCssVar("--text-secondary", theme.textSecondary);
  setCssVar("--text-muted", theme.textMuted);
  setCssVar("--shadow-color", theme.shadow);

  // Status colors: set only if API sends value
  setCssVar("--red", theme.red);
  setCssVar("--yellow", theme.yellow);
  setCssVar("--green", theme.green);
  setCssVar("--error-color", theme.error);
  setCssVar("--status-red", theme.statusRed);
  setCssVar("--status-green", theme.statusGreen);
  setCssVar("--status-orange", theme.statusOrange);
  setCssVar("--status-blue", theme.statusBlue);
  setCssVar("--status-purple", theme.statusPurple);
  setCssVar("--status-dark-red", theme.statusDarkRed);
  setCssVar("--status-dark-green", theme.statusDarkGreen);

  // Borders/backgrounds: no fallback, otherwise dark theme gets overwritten
  setCssVar("--disabled-gray", theme.disabledGray);
  setCssVar("--border-light", theme.borderLight);
  setCssVar("--border-medium", theme.borderMedium);
  setCssVar("--border-dark", theme.borderDark);
  setCssVar("--bg-light", theme.bgLight);
  setCssVar("--bg-lighter", theme.bgLighter);
  setCssVar("--bg-orange-light", theme.bgOrangeLight);
  setCssVar("--bg-green-light", theme.bgGreenLight);
  setCssVar("--bg-red-light", theme.bgRedLight);
  setCssVar("--bg-red-medium", theme.bgRedMedium);
  setCssVar("--bg-purple-light", theme.bgPurpleLight);
  setCssVar("--border-orange-accent", theme.borderOrangeAccent);
  setCssVar("--border-green-accent", theme.borderGreenAccent);
  setCssVar("--border-red-accent", theme.borderRedAccent);
};

// Centralized localStorage helpers
const LS_CHANGE_EVENT = 'ls-change'

const lsGet = (key) => {
	try {
		const item = localStorage.getItem(key)
		return item ? JSON.parse(item) : null
	} catch {
		return null
	}
}

const lsSet = (key, value) => {
	try {
		const oldValue = localStorage.getItem(key)
		localStorage.setItem(key, JSON.stringify(value))
		window.dispatchEvent(new CustomEvent(LS_CHANGE_EVENT, { detail: { key, oldValue, newValue: JSON.stringify(value) } }))
	} catch { }
}

const lsRemove = (key) => {
	try {
		const oldValue = localStorage.getItem(key)
		localStorage.removeItem(key)
		window.dispatchEvent(new CustomEvent(LS_CHANGE_EVENT, { detail: { key, oldValue, newValue: null } }))
	} catch { }
}

const lsClear = () => {
	try {
		localStorage.clear()
		window.dispatchEvent(new CustomEvent(LS_CHANGE_EVENT, { detail: { key: null, oldValue: null, newValue: null } }))
	} catch { }
}

// React state synced with localStorage
function useLocalStorage(key, initialValue) {
	const [storedValue, setStoredValue] = useState(() => {
		const item = lsGet(key)
		return item !== null ? item : initialValue
	})

	const setValue = (value) => {
		setStoredValue(value)
		if (value === undefined || value === null) {
			lsRemove(key)
		} else {
			lsSet(key, value)
		}
	}

	const removeValue = () => {
		setStoredValue(initialValue)
		lsRemove(key)
	}

	return [storedValue, setValue, removeValue]
}

// Listens for localStorage changes in the same tab AND other tabs
function useStorageEvent(key, callback) {
	const callbackRef = useRef(callback)
	callbackRef.current = callback

	useEffect(() => {
		// Other tabs — native browser storage event (e.key === null means localStorage.clear())
		const crossTabHandler = (e) => {
			if (key === null || e.key === key || e.key === null) {
				callbackRef.current({ key: e.key, oldValue: e.oldValue, newValue: e.newValue })
			}
		}
		// Same tab — custom event dispatched by lsSet / lsRemove / lsClear (detail.key === null means lsClear())
		const sameTabHandler = (e) => {
			if (key === null || e.detail.key === key || e.detail.key === null) {
				callbackRef.current({ key: e.detail.key, oldValue: e.detail.oldValue, newValue: e.detail.newValue })
			}
		}
		window.addEventListener('storage', crossTabHandler)
		window.addEventListener(LS_CHANGE_EVENT, sameTabHandler)
		return () => {
			window.removeEventListener('storage', crossTabHandler)
			window.removeEventListener(LS_CHANGE_EVENT, sameTabHandler)
		}
	}, [key])
}

// Redirects to login if token is removed in any tab
function useTokenGuard(loginPath = '/login') {
	useStorageEvent('token', ({ newValue }) => {
		if (newValue === null) {
			window.location.replace(loginPath)
		}
	})
}

export { useDebounce, useWindowOpener, useDebounceWithUseEffectDetection, useApplyTheme, useIframeHandler, useDebounceDeep, useStorageEvent, useLocalStorage, useTokenGuard, lsGet, lsSet, lsRemove, lsClear };
